import { Grid, Button, TextField, DialogContentText, Autocomplete, IconButton, InputAdornment, Box } from '@mui/material';
import { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import Iconify from '../../../../reusable-components/iconify';
import ProductFileAttachmentButton from './ProductFileAttachmentButton';
import FileAttachment from '../../../../reusable-components/file-attachment/FileAttachment';
import ItemAISuggestion from '../../../../reusable-components/itemAISuggestion/itemsuggest';

const DESCRIPTION_WIDE_LINE_RATIO = 0.75;

Step1.propTypes = {
  parentData: PropTypes.object,
  setParentData: PropTypes.func,
  categories: PropTypes.array,
  setCategories: PropTypes.func,
  fromOrder: PropTypes.bool,
  fromCart: PropTypes.bool,
  agoraCategories: PropTypes.array,
  agoraSubcategories: PropTypes.array,
  aiEnabled: PropTypes.bool,
};

export default function Step1({
  parentData,
  setParentData,
  categories,
  setCategories,
  agoraCategories,
  agoraSubcategories,
  aiEnabled = true,
}) {
  const updateProductData = (field, value) => {
    setParentData((prevData) => ({ ...prevData, [field]: value }));
  };

  const[files, setFiles] = useState(parentData?.files || []);
  const hasDescription = Boolean(parentData?.description?.trim());

  const [isDescriptionWide, setIsDescriptionWide] = useState(false);
  const descriptionInputRef = useRef(null);
  const measureCanvasRef = useRef(null);

  useEffect(() => {
    setParentData((prevData) => ({ ...prevData, files }));
  }, [files]);

  useEffect(() => {
    const textarea = descriptionInputRef.current;
    if (!textarea) return;

    const canvas = measureCanvasRef.current ?? (measureCanvasRef.current = document.createElement('canvas'));
    const context = canvas.getContext('2d');
    const style = window.getComputedStyle(textarea);
    context.font = `${style.fontStyle} ${style.fontVariant} ${style.fontWeight} ${style.fontSize}/${style.lineHeight} ${style.fontFamily}`;

    const innerWidth = textarea.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
    const description = parentData?.description || '';
    const hasMultipleLines = description.includes('\n');
    const firstLineWidth = context.measureText(description.split('\n', 1)[0]).width;

    setIsDescriptionWide(hasMultipleLines || firstLineWidth >= innerWidth * DESCRIPTION_WIDE_LINE_RATIO);
  }, [parentData?.description]);

  const sortByName = (items = []) =>
    [...items].sort((a, b) =>
      (a.name ?? '').localeCompare(b.name ?? '', undefined, {
          sensitivity: 'base',
      })
    );

  const sortedCategories = useMemo(
    () =>
        sortByName(agoraCategories),
    [agoraCategories]
  );

 const sortedSubcategories = useMemo(
    () =>
         sortByName(agoraSubcategories),
    [agoraSubcategories]
 );

  return (
    <>
      <Grid container direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
        <Grid item xs={12}>
          <DialogContentText sx={{ fontWeight: 'bold', mt: 2 }}>
            Description *
          </DialogContentText>
          <TextField
            size="small"
            label=""
            fullWidth
            multiline
            inputRef={descriptionInputRef}
            value={parentData?.description || ''}
            onChange={(e) => {
              updateProductData('description', e.target.value);
            }}
            variant="outlined"
            autoComplete="off"
            InputProps={{
              sx: {
                position: 'relative',
                pr: hasDescription ? 6 : 1,
              },
              endAdornment: hasDescription ? (
                <InputAdornment
                  position="end"
                  sx={
                    isDescriptionWide
                      ? {
                          position: 'absolute',
                          bottom: 'calc(100% + 4px)',
                          right: 8,
                          height: 0,
                          alignItems: 'flex-end',
                        }
                      : {
                          position: 'absolute',
                          top: '50%',
                          right: 8,
                          transform: 'translateY(-50%)',
                        }
                  }
                >
                  <ItemAISuggestion
                    description={parentData?.description || ''}
                    aiEnabled={aiEnabled}
                    onSuggestionSelected={(suggestedDescription) => {
                      setParentData((prev) => ({ ...prev, description: suggestedDescription }));
                    }}
                  />
                </InputAdornment>
              ) : null,
            }}
          />
        </Grid>
      </Grid>
      <Grid container direction="row" justifyContent="space-between" alignItems="center">
        <Grid item xs={12}>
          <DialogContentText sx={{ fontWeight: 'bold', mt: 2 }}>
            Notes <ProductFileAttachmentButton setFiles={setFiles} />
          </DialogContentText>{' '}
          <TextField
            size="small"
            label=""
            fullWidth
            multiline
            minRows={2}
            value={parentData?.notes || ''}
            onChange={(e) => {
              updateProductData('notes', e.target.value);
            }}
            variant="outlined"
            autoComplete="off"
          />
          <Box sx={{ mt: 0.3, display: 'flex', flexWrap: 'wrap' }}>
            {parentData?.files?.map((file) => (
            <FileAttachment
              key={file.name}
              file={file}
              fileName={file.name}
              canDeleteFile
              handleClickAttachment={() => window.open(URL.createObjectURL(file), '_blank')}
              handleDeleteFile={() => setFiles((prevFiles) => prevFiles.filter((f) => f.name !== file.name))}
            />))} 
          </Box>
        </Grid>
      </Grid>
      {categories.map((category, index) => (
        <Grid
          key={`${index}-${category.agoraCategoryId}-${category.agoraSubcategoryId}`}
          container
          direction="row"
          justifyContent="space-between"
          alignItems="flex-end"
        >
          <Grid item xs={6}>
            <DialogContentText sx={{ fontWeight: 'bold', mt: 2 }}>Category *</DialogContentText>{' '}
            <Autocomplete
              size="small"
              sx={{
                '& legend': { display: 'none' },
                '& fieldset': { top: 0 },
                '& .MuiFormLabelRoot ': 'none',
                width: '90%',
              }}
              type="new-password"
              options={sortedCategories}
              autoHighlight
              getOptionLabel={(option) => option?.name}
              value={agoraCategories?.find((cat) => cat.id === categories[index].agoraCategoryId)}
              onChange={(event, newValue) => {
                setCategories((prevCategories) => {
                  const newCategories = [...prevCategories];
                  newCategories[index].agoraCategoryId = newValue?.id;
                  return newCategories;
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label=""
                  variant="outlined"
                  autoComplete="off"
                />
              )}
            />
          </Grid>
          <Grid item xs={5}>
            <DialogContentText sx={{ fontWeight: 'bold', mt: 2 }}>Subcategory *</DialogContentText>{' '}
            <Autocomplete
              size="small"
              disabled={!categories[index].agoraCategoryId}
              sx={{
                '& legend': { display: 'none' },
                '& fieldset': { top: 0 },
                '& .MuiFormLabelRoot ': 'none',
                width: '90%',
              }}
              type="new-password"
              options={
                  sortedSubcategories.filter((cat) => cat.mainCategoryId === categories[index].agoraCategoryId) || []
              }
              autoHighlight
              getOptionLabel={(option) => option?.name}
              value={agoraSubcategories.find((cat) => cat.id === categories[index].agoraSubcategoryId)}
              onChange={(event, newValue) => {
                setCategories((prevCategories) => {
                  const newCategories = [...prevCategories];
                  newCategories[index].agoraSubcategoryId = newValue?.id;
                  return newCategories;
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label=""
                  variant="outlined"
                  autoComplete="off"
                />
              )}
            />
          </Grid>
          <Grid
            item
            xs={1}
            sx={{
              mt: 4,
            }}
          >
            <IconButton
              onClick={() => {
                if (categories.length === 1) {
                  setCategories([{ agoraCategoryId: null, agoraSubcategoryId: null }]);
                } else {
                  setCategories((prevCategories) => {
                    const newCategories = [...prevCategories];
                    newCategories?.splice(index, 1);
                    return newCategories;
                  });
                }
              }}
            >
              <Iconify icon="material-symbols:delete-outline" width={20} height={20} color="error" />
            </IconButton>
          </Grid>
        </Grid>
      ))}
        <Grid container direction="row" justifyContent="space-between" alignItems="center">
          <Button
            variant="text"
            color="primary"
            disabled={categories.some((category) => !category.agoraCategoryId || !category.agoraSubcategoryId)}
            sx={{
              mt: 2,
              textDecoration: 'underline',
            }}
            onClick={() => setCategories([...categories, { agoraCategoryId: null, agoraSubcategoryId: null }])}
          >
            Add additional category
          </Button>
        </Grid>
    </>
  );
}

